import React, { useState, useEffect } from "react";
import { Box, Button, Typography, styled, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";

const CookieConsentContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: "#ffffff",
  border: "1px solid #bfe5ec",
  borderRadius: "16px 16px 0 0",
  boxShadow: "0px -4px 24px 0px rgba(10,130,157,0.15)",
  padding: "24px 32px",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "24px",
  maxWidth: "100vw",
  margin: "0 auto",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: "20px",
    padding: "24px 20px",
    borderRadius: "12px 12px 0 0",
  },
  [theme.breakpoints.down("sm")]: {
    gap: "16px",
    padding: "20px 16px",
    borderRadius: "8px 8px 0 0",
  },
  [theme.breakpoints.down("xs")]: {
    padding: "16px 12px",
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  [theme.breakpoints.down("sm")]: {
    gap: "6px",
  },
}));

const CookieTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "20px",
  color: "#033843",
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
    lineHeight: "22px",
  },
}));

const CookieText = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#525252",
  [theme.breakpoints.down("sm")]: {
    fontSize: "16px",
    lineHeight: "24px",
  },
}));

const PolicyLink = styled("span")(({ theme }) => ({
  color: "#0a829d",
  textDecoration: "underline",
  cursor: "pointer",
  fontWeight: 500,
  padding: "2px 4px",
  borderRadius: "4px",
  "&:hover": {
    color: "#086e86",
    backgroundColor: "rgba(10, 130, 157, 0.05)",
  },
  [theme.breakpoints.down("sm")]: {
    display: "inline-block",
    minHeight: "44px",
    lineHeight: "40px",
    padding: "2px 8px",
  },
}));

const ButtonSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: "12px",
    alignItems: "stretch",
  },
}));

const AcceptButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)",
  border: "1px solid #ffffff",
  borderRadius: "32px",
  height: "44px",
  padding: "10px 24px",
  minWidth: "120px",
  boxShadow: "0px 2px 12px 0px rgba(10,130,157,0.2)",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  color: "#ffffff",
  "&:hover": {
    background: "linear-gradient(90deg, #086e86 0%, #2bc9d6 100%)",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    height: "48px",
    minHeight: "48px",
    fontSize: "16px",
    borderRadius: "24px",
    order: 1,
  },
}));

const DeclineButton = styled(Button)(({ theme }) => ({
  background: "transparent",
  border: "1px solid #bfe5ec",
  borderRadius: "32px",
  height: "44px",
  padding: "10px 24px",
  minWidth: "120px",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  color: "#0a829d",
  "&:hover": {
    backgroundColor: "#f0f9fa",
    borderColor: "#0a829d",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    height: "48px",
    minHeight: "48px",
    fontSize: "16px",
    borderRadius: "24px",
    order: 2,
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "12px",
  right: "12px",
  width: "32px",
  height: "32px",
  color: "#737373",
  "&:hover": {
    backgroundColor: "rgba(115, 115, 115, 0.04)",
  },
  [theme.breakpoints.down("sm")]: {
    width: "44px",
    height: "44px",
    top: "8px",
    right: "8px",
  },
}));

interface CookieConsentProps {
  onAccept?: () => void;
  onDecline?: () => void;
}

export default function CookieConsent({ onAccept, onDecline }: CookieConsentProps) {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem("cookieConsent");
    if (!cookieConsent) {
      // Show popup after a short delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "accepted");
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    setIsVisible(false);
    onAccept?.();
  };

  const handleDecline = () => {
    localStorage.setItem("cookieConsent", "declined");
    localStorage.setItem("cookieConsentDate", new Date().toISOString());
    setIsVisible(false);
    onDecline?.();
  };

  const handleClose = () => {
    // Close without making a choice - will show again on next visit
    setIsVisible(false);
  };

  const handlePrivacyPolicyClick = () => {
    navigate("/privacy-policy");
  };

  if (!isVisible) {
    return null;
  }

  return (
    <CookieConsentContainer>
      <CloseButton onClick={handleClose}>
        <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 4L4 12M4 4L12 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </CloseButton>
      
      <ContentSection>
        <CookieTitle>We use cookies</CookieTitle>
        <CookieText>
          We use cookies and similar technologies to improve user experience, analyze usage, and personalize content. 
          By continuing to use our site, you consent to our use of cookies. Learn more in our{" "}
          <PolicyLink onClick={handlePrivacyPolicyClick}>Privacy Policy</PolicyLink>.
        </CookieText>
      </ContentSection>
      
      <ButtonSection>
        <DeclineButton onClick={handleDecline}>
          Decline
        </DeclineButton>
        <AcceptButton onClick={handleAccept}>
          Accept All
        </AcceptButton>
      </ButtonSection>
    </CookieConsentContainer>
  );
}